import { Controller, useFormContext } from 'react-hook-form'

import { Box } from '@factorialco/deprecated-design-system'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { fromCents } from 'modules/core/lib/formatCurrencyUtils'

import PerDiemRateField from 'modules/expenses/components/expenses/Form/PerDiemRateField'
import { PerDiemRate, usePerDiemForm } from 'modules/expenses/hooks/usePerDiemForm'
import { getLabelFromRate } from 'modules/expenses/lib/expensables/expensable'

import { castError } from '../../castError'
import withCustomizationFields, {
  WithCustomizationFieldsProps,
} from '../../withCustomizationFields'
import withFieldPermissions, { WithFieldPermissionsProps } from '../../withFieldPermissions'

const FIELD_NAME = 'perDiemRates'

interface Props extends WithCustomizationFieldsProps, WithFieldPermissionsProps {
  selectedRate: PerDiemRate
  loading?: boolean
  onSelect?: (rateConfigurationId: number | null) => void
}

const PerDiemRateFieldComponent = ({
  fieldIdentifier,
  selectedRate,
  loading = false,
  onSelect,
  isFieldReadOnly,
  isFieldEnabled,
  isFieldMandatory,
}: Props) => {
  const {
    control,
    getValues,
    setValue,
    formState: { errors },
  } = useFormContext()

  const { company, access } = useCurrentSession()

  const { rateConfigurations, loading: loadingPerDiemForm } = usePerDiemForm(company.id, access.id)

  // Early return if field is not enabled
  if (!isFieldEnabled) return null

  const rateIds = getValues('perDiemRates').map((r: PerDiemRate) => r.rateConfigurationId)

  const rates = rateConfigurations.filter((r) => !rateIds.includes(r.id))

  const handleSelectRate = (rateConfigurationId: number | null) => {
    setValue(fieldIdentifier, rateConfigurationId)
    onSelect?.(rateConfigurationId)
  }

  return (
    <Box width="full">
      <Controller
        name={fieldIdentifier}
        control={control}
        render={({ field }) => {
          return (
            <PerDiemRateField
              readonly={isFieldReadOnly}
              selectedRate={selectedRate}
              mandatory={isFieldMandatory}
              perDiemRatesOptions={(rates || []).map((r) => {
                return {
                  label: getLabelFromRate(
                    access.locale,
                    r.currency,
                    r.amountCents ? fromCents(r.amountCents) : 0,
                    r.name
                  ),
                  value: r.id,
                }
              })}
              loading={loading || loadingPerDiemForm}
              onSelect={handleSelectRate}
              error={castError(errors?.[fieldIdentifier])}
            />
          )
        }}
      />
    </Box>
  )
}

export default withCustomizationFields(withFieldPermissions(PerDiemRateFieldComponent, FIELD_NAME))
